import { uuidv4 } from '@firebase/util';
import { useState } from 'react';

import { nodemapToTree } from '../../utils/nodemap-to-tree';
import { MIN_NODE_HEIGTH, MIN_NODE_WIDTH } from './../../components/map/node';
import {
  FileAttachement,
  FlatNode,
  LinkAttachement,
  NodeAttachement,
  PartialFileAttachement,
  PartialLinkAttachement,
  Tree,
} from './types';
import { InteractableMap } from './use-interactable-map';
import { Canvas } from './use-mindmap-canvas';
import { Data } from './use-mindmap-data';
import { Selection } from './use-mindmap-selection';
import { Viewport } from './use-mindmap-viewport';

export type ManipulationActions = {
  onDeleteClick: () => void;
  onAddChildClick: () => void;
  onAddTopSiblingClick: () => void;
  onAddBottomSiblingClick: () => void;
  onAddLinkAttachementClick: (attachement: PartialLinkAttachement | undefined) => void;
  onAddFileAttachementClick: (attachement: PartialFileAttachement | undefined) => void;
  deleteSelectable: boolean;
  addChildSelectable: boolean;
  addTopSiblingSelectable: boolean;
  addBottomSiblingSelectable: boolean;
  addAttachementSelectable: boolean;
};

export interface Manipulation {
  shadowNodeAttachementsWidth: number | undefined;
  setShadowNodeAttachementsWidth: React.Dispatch<React.SetStateAction<number | undefined>>;
  addChild: (...ids: string[]) => FlatNode[];
  addSibling: (left: boolean, ...ids: string[]) => FlatNode[];
  removeNode: (...ids: string[]) => void;
  actions: ManipulationActions;
  deleteAttachement: (nodeId: string, attachementId: string) => void;
  updateAttachement: (nodeId: string, attachement: NodeAttachement) => void;
  updateText: (nodeId: string, changedText: { html: string; width: number; height: number }) => void;
  calculateMinAttachementDimensions: (node: FlatNode) => FlatNode;
}

type UseManipulation = {
  data: Data;
  background: Canvas;
  viewport: Viewport;
  selection: Selection;
  interactableMap: InteractableMap;
};

export const useMindmapManipulation = ({
  data: { nodeMap, setNodeMap, rootNodeId },
  background: { removeLine },
  viewport: { moveIntoViewport },
  selection: { selectedNodeIds },
  interactableMap: { moveContextMenuMapCoords },
}: UseManipulation): Manipulation => {
  const [shadowNodeAttachementsWidth, setShadowNodeAttachementsWidth] = useState<number>();

  const addChild = (...ids: string[]): FlatNode[] => {
    let newNodeMap = { ...nodeMap };
    let createdChildren: FlatNode[] = [];

    ids.forEach((id) => {
      let newUuid = uuidv4();
      const textWidth = MIN_NODE_WIDTH;
      const textHeight = MIN_NODE_HEIGTH;

      const newChild: FlatNode = {
        version: parseInt(process.env.REACT_APP_MINDMAP_VERSION || '1'),
        id: newUuid,
        pid: id,
        width: textWidth,
        height: textHeight,
        type: 'text',
        attachementMinHeight: 0,
        attachementMinWidth: 0,
        previewMinHeight: 0,
        previewMinWidth: 0,
        textMinHeight: textHeight,
        textMinWidth: textWidth,
        text: '',
      };
      newNodeMap[newUuid] = newChild;
      createdChildren.push(newChild);
    });

    setNodeMap(newNodeMap);
    moveIntoViewport(createdChildren.map(({ id }) => id));
    return createdChildren;
  };

  const addSibling = (left: boolean, ...ids: string[]): FlatNode[] => {
    let newNodeMap = { ...nodeMap };
    let createdSiblings: FlatNode[] = [];

    // filter out map root node id
    ids = ids.filter((id) => id != rootNodeId);

    ids.forEach((id) => {
      let newUuid = uuidv4();
      const textWidth = MIN_NODE_WIDTH;
      const textHeight = MIN_NODE_HEIGTH;

      const newChild: FlatNode = {
        version: parseInt(process.env.REACT_APP_MINDMAP_VERSION || '1'),
        id: newUuid,
        pid: nodeMap[id].pid,
        width: textWidth,
        height: textHeight,
        type: 'text',
        attachementMinHeight: 0,
        attachementMinWidth: 0,
        previewMinHeight: 0,
        previewMinWidth: 0,
        textMinHeight: textHeight,
        textMinWidth: textWidth,
        text: '',
      };
      newNodeMap = left
        ? {
            [newUuid]: newChild,
            ...nodeMap,
          }
        : {
            ...nodeMap,
            [newUuid]: newChild,
          };
      createdSiblings.push(newChild);
    });

    setNodeMap(newNodeMap);
    moveIntoViewport(createdSiblings.map(({ id }) => id));
    return createdSiblings;
  };

  const removeNode = (...ids: string[]) => {
    if (nodeMap) {
      let newNodeMap = { ...nodeMap };
      let linesToDelete: string[] = [];

      // filter out map root node id
      ids = ids.filter((id) => id != rootNodeId);

      ids.forEach((id) => {
        // get node's subtree
        let subtree = nodemapToTree(newNodeMap, ({ id: _id }) => _id === id);

        // delete node itself
        linesToDelete.push(id);
        delete newNodeMap[id];

        // delete all child nodes
        if (subtree) {
          let children = subtree.children;
          if (children) {
            let child: Tree | undefined;
            while ((child = children.shift())) {
              delete newNodeMap[child.id];
              linesToDelete.push(child.id);

              if (child.children) {
                children.push(...child.children);
              }
            }
          }
        }
      });

      // update flat node map
      setNodeMap(newNodeMap);

      // remove lines
      removeLine(...linesToDelete);
    }
  };

  const updateText = (nodeId: string, changedText: { html: string; width: number; height: number }) => {
    setNodeMap((nodeMap) => {
      let newNode = nodeMap[nodeId];
      newNode.text = changedText.html;
      newNode.textMinWidth = changedText.width;
      newNode.textMinHeight = changedText.height;

      newNode = calculateNodeDimensions(newNode);

      return { ...nodeMap, [nodeId]: newNode };
    });
  };

  const deleteAttachement = (nodeId: string, attachementId: string) => {
    if (nodeId) {
      const node = nodeMap[nodeId];
      let newNode: FlatNode = {
        ...node,
        attachements: node.attachements?.filter((a) => a.id !== attachementId),
      };

      const attachement = node.attachements?.find(({ id }) => id == attachementId);
      if (attachement?.type == 'file' && attachement.isPreview) {
        // unset preview
        newNode.previewMinHeight = 0;
      }

      // update shadow node text element width because text breaks
      // can change if attachement width changes
      const { attachementMinWidth } = calculateMinAttachementDimensions(newNode);
      let nodeTextShadow = document.getElementById(`node-text-shadow-${nodeId}`);
      if (nodeTextShadow) {
        // set width directly
        nodeTextShadow.style.minWidth = String(
          Math.max(
            MIN_NODE_WIDTH,
            node.previewMinWidth,
            attachementMinWidth === 0 ? attachementMinWidth : attachementMinWidth || node.attachementMinWidth || 0,
          ) + 'px',
        );
      }

      newNode = calculateNodeDimensions(newNode);

      setNodeMap({ ...nodeMap, [nodeId]: newNode });
    }
  };

  const updateAttachement = (nodeId: string, attachement: NodeAttachement) => {
    let newNode = nodeMap[nodeId];
    const prevPreviewMinHeight = newNode.previewMinHeight;
    newNode.attachements = newNode.attachements?.map((a) => (a.id === attachement.id ? { ...a, ...attachement } : a));

    const attachementPreviewEnabled = attachement.type == 'file' && attachement.isPreview;
    if (attachementPreviewEnabled) {
      // set preview
      newNode.previewMinHeight = attachement.filePreviewHeight;
      // unset other attachement previews
      newNode.attachements = newNode.attachements?.map((a) =>
        a.id === attachement.id ? a : { ...a, isPreview: false },
      );
    }
    const nodePreviewDisabled = !newNode.attachements?.find(
      (attachement) => attachement.type === 'file' && attachement.isPreview,
    );
    if (nodePreviewDisabled) {
      // unset preview
      newNode.previewMinHeight = 0;
    }

    newNode = calculateNodeDimensions(newNode);

    setNodeMap({ ...nodeMap, [nodeId]: newNode });

    // move context menu when image/file preview was enabled or disabled
    const hadPrevMinHeight = !!prevPreviewMinHeight;
    const hasCurrMinHeight = !!newNode.previewMinHeight;
    if (hadPrevMinHeight != hasCurrMinHeight && attachement.type == 'file') {
      moveContextMenuMapCoords(0, hadPrevMinHeight ? -attachement.filePreviewHeight : attachement.filePreviewHeight);
    }
  };

  const addAttachement = (nodeId: string, attachement: NodeAttachement) => {
    let newNode = nodeMap[nodeId];
    newNode.attachements = [...(newNode.attachements || []), attachement];

    newNode = calculateNodeDimensions(newNode);

    setNodeMap({ ...nodeMap, [nodeId]: newNode });
  };

  const calculateNodeDimensions = (node: FlatNode, skipAttachementsDimensions?: boolean) => {
    // get text dimensions of shadow node element
    const nodeTextShadow = document.getElementById(`node-text-shadow-${node.id}`);
    const textWidth = nodeTextShadow?.clientWidth;
    node.textMinWidth = textWidth || 0;
    const textHeight = nodeTextShadow?.clientHeight;
    node.textMinHeight = textHeight || 0;

    // calculate attachement dimensions
    if (!skipAttachementsDimensions) node = calculateMinAttachementDimensions(node);

    // calculate width and height
    const width = Math.max(node.textMinWidth, node.previewMinWidth, node.attachementMinWidth);
    const height = node.textMinHeight + node.previewMinHeight + node.attachementMinHeight;
    return { ...node, width: parseFloat(width.toPrecision(12)), height: parseFloat(height.toPrecision(12)) };
  };

  const ATTACHEMENT_CONTAINER_PADDING = 12;
  const ATTACHEMENT_CHIP_PADDING = 6;
  const ATTACHEMENT_CHIP_HEIGHT = 36;

  // calculate the minimal attachement dimensions of a node
  const calculateMinAttachementDimensions = (node: FlatNode) => {
    const { attachements } = node;
    if (attachements && attachements.length > 0) {
      // min attachement width is the width of the largest attachement or the width
      // of the first two chips (to show the first two chips on a line)
      node.attachementMinWidth =
        Math.max(
          ...attachements.map(({ width }) => width),
          attachements.length >= 2 ? attachements[0]?.width + attachements[1]?.width + 2 * ATTACHEMENT_CHIP_PADDING : 0,
        ) +
        2 * ATTACHEMENT_CONTAINER_PADDING +
        2;

      // calculate min attachement height
      const { width: nodeWidth } = calculateNodeDimensions(node, true);
      let attachementMinHeight = 2 * ATTACHEMENT_CONTAINER_PADDING + ATTACHEMENT_CHIP_HEIGHT;
      let currentRowWidth = 0;
      const attachementContainerWidth = nodeWidth - 2 - 2 * ATTACHEMENT_CONTAINER_PADDING;
      attachements.forEach(({ width }) => {
        if (currentRowWidth + width <= attachementContainerWidth) {
          // this row still has place for a chip
          currentRowWidth += width + ATTACHEMENT_CHIP_PADDING;
        } else {
          // chip gets put on new row
          attachementMinHeight += ATTACHEMENT_CHIP_HEIGHT + ATTACHEMENT_CHIP_PADDING;
          currentRowWidth = width + ATTACHEMENT_CHIP_PADDING;
        }
      });
      node.attachementMinHeight = attachementMinHeight;

      return node;
    } else {
      return { ...node, attachementMinWidth: 0, attachementMinHeight: 0 };
    }
  };

  const MIN_LINK_ATTACHEMENT_CHIP_WIDTH = 48;
  const MIN_FILE_ATTACHEMENT_CHIP_WIDTH = 56;

  const onAddLinkAttachementClick = (attachement: PartialLinkAttachement | undefined) => {
    const newAttachement: LinkAttachement = {
      id: uuidv4(),
      name: '',
      type: 'link',
      url: '',
      width: MIN_LINK_ATTACHEMENT_CHIP_WIDTH,
      ...attachement,
    };
    addAttachement(selectedNodeIds[0], newAttachement);
  };

  const onAddFileAttachementClick = (attachement: PartialFileAttachement | undefined) => {
    const newAttachement: FileAttachement = {
      id: uuidv4(),
      name: '',
      type: 'file',
      isPreview: false,
      filePreviewHeight: 0,
      url: '',
      width: MIN_FILE_ATTACHEMENT_CHIP_WIDTH,
      ...attachement,
    };
    addAttachement(selectedNodeIds[0], newAttachement);
  };

  const disabled = selectedNodeIds.length == 0;
  const rootNodeSelected = rootNodeId == selectedNodeIds[0];
  const actions: ManipulationActions = {
    onDeleteClick: () => removeNode(...selectedNodeIds),
    onAddChildClick: () => addChild(...selectedNodeIds),
    onAddTopSiblingClick: () => addSibling(false, ...selectedNodeIds),
    onAddBottomSiblingClick: () => addSibling(false, ...selectedNodeIds),
    onAddLinkAttachementClick,
    onAddFileAttachementClick,
    addBottomSiblingSelectable: disabled || rootNodeSelected,
    addTopSiblingSelectable: disabled || rootNodeSelected,
    addChildSelectable: disabled,
    deleteSelectable: disabled || rootNodeSelected,
    addAttachementSelectable: disabled,
  };

  return {
    addChild,
    addSibling,
    removeNode,
    actions,
    deleteAttachement,
    updateAttachement,
    updateText,
    shadowNodeAttachementsWidth,
    setShadowNodeAttachementsWidth,
    calculateMinAttachementDimensions,
  };
};
